import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type ColumnDef } from '@tanstack/react-table';
import { useToggle } from 'usehooks-ts';

import {
  getGetFolderRoleAssignmentsByUserQueryKey,
  type LocationUserRolesAssignment,
  useDeleteRoleAssignment,
  useGetFolder,
  useGetFolderRoleAssignmentsByUser,
  useGetOrganizationBySlug
} from '@/api/generated';
import PlusIcon from '@/assets/icons/plus.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import DataGrid from '@/components/DataGrid/DataGrid';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';
import ClientUserDropdownSelect from '@/views/fields/UserDropdownSelect/ClientUserDropdownSelect';
import InviteClientUsersModal from '@/views/Users/InviteClientUsersModal';

import { folderAccessColumns } from './folderAccessColumns';

const MAX_WIDTH = 1000;

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiTableContainer-root': {
    boxShadow: 'none',
    border: 'none',
    borderRadius: 0
  },
  '& .MuiTable-root': {
    border: 'none'
  },
  '& .MuiTableHead-root': {
    boxShadow: 'none',
    '& .MuiTableCell-root': {
      borderTop: 'none',
      borderLeft: borders.default,
      borderRight: borders.default,
      borderBottom: borders.default,
      borderColor: theme.palette.grey[300]
    },
    '& .MuiTableCell-root:first-of-type': {
      borderLeft: 'none'
    },
    '& .MuiTableCell-root:last-of-type': {
      borderRight: 'none'
    }
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '& .MuiTableCell-root': {
        borderTop: 'none',
        borderLeft: borders.default,
        borderRight: borders.default,
        borderBottom: borders.default,
        borderColor: theme.palette.grey[300]
      },
      '& .MuiTableCell-root:first-of-type': {
        borderLeft: 'none'
      },
      '& .MuiTableCell-root:last-of-type': {
        borderRight: 'none'
      }
    }
  }
}));

interface FolderAccessSettingsProps {
  organizationSlug?: string;
  locationSlug?: string;
}

const FolderAccessSettings: FC<FolderAccessSettingsProps> = ({ organizationSlug, locationSlug }) => {
  const { data: organization } = useGetOrganizationBySlug(
    organizationSlug!,
    {},
    {
      query: {
        enabled: !!organizationSlug
      }
    }
  );
  const { data: folder } = useGetFolder(organizationSlug!, locationSlug!, {
    query: {
      enabled: !!organizationSlug && !!locationSlug
    }
  });
  const { t } = useTranslation('settings');
  const { spacing } = useTheme();
  const [showInviteModal, toggleInviteModal] = useToggle();
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();
  const [showConfirmationModal, toggleConfirmationModal] = useToggle();
  const [roleAssignmentToRemove, setRoleAssignmentToRemove] = useState<LocationUserRolesAssignment | null>(null);
  const { mutateAsync: deleteRoleAssignment } = useDeleteRoleAssignment();
  const roleAssignmentIdToRemove = roleAssignmentToRemove?.id;
  const handleDeleteRoleAssignment = useCallback(async () => {
    try {
      setIsDeleting(true);
      if (roleAssignmentIdToRemove) {
        await deleteRoleAssignment({ roleAssignmentId: roleAssignmentIdToRemove });
      }
      setRoleAssignmentToRemove(null);
      toggleConfirmationModal();
      await queryClient.invalidateQueries(getGetFolderRoleAssignmentsByUserQueryKey(organizationSlug!, locationSlug!));
    } finally {
      setIsDeleting(false);
    }
  }, [
    roleAssignmentIdToRemove,
    toggleConfirmationModal,
    queryClient,
    organizationSlug,
    locationSlug,
    deleteRoleAssignment
  ]);

  const { data: roleAssignments } = useGetFolderRoleAssignmentsByUser(
    organizationSlug!,
    locationSlug!,
    {},
    {
      query: {
        enabled: !!organizationSlug && !!locationSlug
      }
    }
  );

  const handleRemove = useCallback(
    (userAccess: LocationUserRolesAssignment) => {
      setRoleAssignmentToRemove(userAccess);
      toggleConfirmationModal();
    },
    [setRoleAssignmentToRemove, toggleConfirmationModal]
  );

  const columns = useMemo(() => folderAccessColumns(t, handleRemove), [t, handleRemove]);

  const admins = useMemo(
    () => organization?.admins?.filter(admin => admin?.id)?.map(admin => admin.id!) ?? [],
    [organization]
  );

  if (!folder || !organization || !roleAssignments) {
    return <BackdropLoader />;
  }

  return (
    <Stack gap={1} width="100%">
      <ClientUserDropdownSelect
        name="admins"
        value={admins}
        label={t('orgSettings.fields.organizationAdmins')}
        usePortal
        isDisabled
        sx={{
          width: '100%',
          pb: 2,
          '& .DropdownSelect-control': {
            bgcolor: 'grey.200',
            border: borders.half,
            borderColor: 'grey.400'
          }
        }}
      />
      <Divider orientation="horizontal" sx={{ borderColor: 'grey.400', mb: 2 }} />
      <Stack direction="row" justifyContent="end" alignItems="center" width="100%">
        <Button
          id="add-user"
          variant="outlined"
          sx={{
            ...typography.body2,
            fontWeight: typography.fontWeightMedium,
            py: 0.5,
            paddingInlineStart: 1,
            paddingInlineEnd: 1.5,
            '& .MuiButton-icon': {
              marginInlineStart: 0,
              marginInlineEnd: 1
            },
            '& .PlusIcon-root': { width: spacing(2), height: spacing(2) }
          }}
          startIcon={<PlusIcon className="PlusIcon-root" />}
          onClick={toggleInviteModal}
        >
          {t('orgSettings.folderAccess.addUser')}
        </Button>
      </Stack>
      <Box sx={{ px: 3, flexGrow: 1, maxWidth: MAX_WIDTH }}>
        <StyledDataGrid
          data={roleAssignments?.locationUserRolesAssignments || []}
          columns={columns as ColumnDef<object>[]}
          isLoading={false}
          isLoadingNextPage={false}
          hasStickyRow={false}
        />
      </Box>
      <ConfirmationModal
        open={showConfirmationModal}
        onConfirm={handleDeleteRoleAssignment}
        onDiscard={toggleConfirmationModal}
        onClose={toggleConfirmationModal}
        title={
          <Typography variant="body2">
            {t('orgSettings.folderModal.removeAccessConfirmationPrefix')}{' '}
            <Typography variant="body2" sx={{ fontWeight: typography.fontWeightMedium }}>
              {t('orgSettings.folderModal.removeAccessBold', {
                folderName: folder?.name || '',
                userName: roleAssignmentToRemove?.user?.name || roleAssignmentToRemove?.user?.email || ''
              })}
            </Typography>
          </Typography>
        }
        confirmButtonLabel={t('orgSettings.folderModal.removeAccess')}
        discardButtonLabel={t('common:actions.cancel')}
        illustration={<Box component="img" src="/images/alarm.svg" alt="Alarm" sx={{ width: 133, height: 128 }} />}
        isLoading={isDeleting}
        loadingLabel={t('orgSettings.folderModal.removingAccess')}
      />
      {showInviteModal && (
        <InviteClientUsersModal
          onClose={toggleInviteModal}
          defaultOrganizationSlug={organizationSlug}
          defaultLocationSlug={locationSlug}
        />
      )}
    </Stack>
  );
};

export default FolderAccessSettings;
